import Phaser from 'phaser';

import type { Circuit, PlayerTrackPosition } from '@multirx/shared/types/track.js';

import type {
  RaceHistoryParticipant,
  RaceHistoryPlayerSnapshot,
  RaceHistoryReplay,
} from '@multirx/shared/types/raceHistory.js';

import { getCircuitDisplayBounds } from '@multirx/shared/game/circuitEdit.js';
import {
  getTrackForwardDelta,
  trackPositionGridKey,
  trackPositionsEqual,
} from '@multirx/shared/game/trackMovement.js';
import { lerpAlongPolyline } from '@multirx/shared/game/archivePathLerp.js';
import {
  diceMovesForRound,
  mergeReplayCircuit,
  normalizeStoredCircuit,
  rankingAtRound,
  resolveBoardAfterRound,
} from '@multirx/shared/game/archiveReplay.js';
import { formatRaceRankLabel } from '@multirx/shared/game/carHudBadge.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import {
  ARCHIVE_ROUND_MS_DEFAULT,
  archivePathDurationMs,
  clampArchiveRoundMs,
  readArchiveRoundMs,
} from '../raceHistory/archiveRoundDuration.js';
import type { MovePlayerToOptions } from '../circuits/circuit3dPreview.js';

import { TrackRenderer } from './track/TrackRenderer.js';
import {
  calculateTrackZoomFit,
  GAME_TRACK_CELL_SIZE,
  TRACK_FIT_PADDING,
} from './track/trackCircuitRender.js';
import {
  applyTokenDisplaySize,
  applyTokenHitArea,
  createCarToken,
  createCarTokenFromImage,
  type TokenCarStyle,
} from './TokenRenderer.js';
import { tokenDisplaySize } from './tokenCellLayout.js';
import {
  type CellOccupant,
  layoutsForOccupantsOnCell,
  occupantsOnGridKey,
} from './sharedCellLayout.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { readGameCanvasColor } from '../ui/theme.js';
import {
  archiveWaypointsBetween,
  snapshotToTrackPosition,
} from '../raceHistory/archiveReplayTrack.js';

const TRACK_CELL_SIZE = GAME_TRACK_CELL_SIZE;
const FIT_PADDING = TRACK_FIT_PADDING;
const ZOOM_STEP = 0.2;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 6;
const TOKEN_DEPTH = 10;
const HUD_LABEL_DEPTH = 12;
const PLAYER_COLORS = [
  0xef4444, 0x22c55e, 0x3b82f6, 0xeab308, 0xa855f7, 0xec4899, 0xf97316, 0x14b8a6, 0x6366f1,
  0x84cc16,
];

export type ArchiveReplaySceneData = {
  circuit: Circuit;
  replay: RaceHistoryReplay;
  numParticipants: number;
  initialTurn?: number;
};

type TokenLayout = { x: number; y: number; displaySize: number };

export class ArchiveReplayScene extends Phaser.Scene {
  private circuit!: Circuit;
  private replay!: RaceHistoryReplay;
  private numParticipants = 2;
  private trackRenderer: TrackRenderer | null = null;
  private playerPathIndices: number[] = [];
  private playerJokerBranchIndex: (number | null)[] = [];
  private playerStartLaneIndex: (number | null)[] = [];
  private playerJokerLapUsed: boolean[] = [];
  private playerFinishedRace: boolean[] = [];
  private playerTokens: (Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics)[] = [];
  /** Bulles place + nom au-dessus des pions (vue 2D). */
  private playerHudLabels: (Phaser.GameObjects.Text | null)[] = [];
  /** Proxies tween Linear (kill propre entre tours). */
  private animProxies: { t: number }[] = [];
  private tokenStyle: TokenCarStyle = 'ludo';
  private sceneReady = false;
  private pendingTurn: number | null = null;
  private cameraFitted = false;
  /** L’utilisateur a zoomé / déplacé la vue — ne plus recentrer automatiquement. */
  private userViewAdjusted = false;
  private isPanning = false;
  private lastPanX = 0;
  private lastPanY = 0;
  private carTexturesReady = false;
  private lastTrackLayoutKey = '';
  private lastCameraLayoutKey = '';
  private displayedRound = 0;
  private animGeneration = 0;
  /** Durée forcée d’un tour (slider replay) — toutes les voitures partagent la même clock. */
  private roundDurationMs = ARCHIVE_ROUND_MS_DEFAULT;
  /** Notifié de chaque position (case franchie/finale) — même contrat que `GameScene.setLive3DSyncHandler`. */
  private sync3DHandler:
    | ((
        playerIndex: number,
        trackPos: PlayerTrackPosition,
        durationMs: number,
        options?: MovePlayerToOptions
      ) => void)
    | null = null;
  private readonly onScaleResize = (): void => {
    this.refreshView(false);
  };
  private readonly onArchiveReplayResize = (): void => {
    this.refreshView(false);
  };
  private wheelHandler: ((e: WheelEvent) => void) | null = null;

  constructor() {
    super({ key: 'ArchiveReplayScene' });
  }

  init(data: ArchiveReplaySceneData): void {
    this.replay = mergeReplayCircuit(data.replay, data.circuit);
    const resolvedCircuit = this.replay.circuit ?? data.circuit;
    this.circuit = normalizeStoredCircuit(
      resolvedCircuit?.path?.length ? resolvedCircuit : { path: [], width: 1, height: 1, grid: [] }
    );
    this.numParticipants = data.numParticipants;
    const n = Math.max(2, data.numParticipants);
    this.playerPathIndices = Array(n).fill(0);
    this.playerJokerBranchIndex = Array(n).fill(null);
    this.playerStartLaneIndex = Array(n).fill(null);
    this.playerJokerLapUsed = Array(n).fill(false);
    this.playerFinishedRace = Array(n).fill(false);
    this.animProxies = [];
    this.sceneReady = false;
    this.pendingTurn = data.initialTurn ?? 1;
    this.displayedRound = 0;
    this.animGeneration = 0;
    this.lastTrackLayoutKey = '';
    this.lastCameraLayoutKey = '';
    this.userViewAdjusted = false;
    this.roundDurationMs = readArchiveRoundMs();
    this.registry.set('archiveInitialTurn', data.initialTurn ?? 1);
  }

  shutdown(): void {
    this.scale.off('resize', this.onScaleResize);
    this.events.off('archive-replay-resize', this.onArchiveReplayResize);
    const canvas = this.sys.game.canvas;
    if (canvas && this.wheelHandler) {
      canvas.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    this.stopTokenAnimations();
    this.isPanning = false;
    this.trackRenderer?.destroy();
    this.trackRenderer = null;
    this.sceneReady = false;
    this.cameraFitted = false;
    this.carTexturesReady = false;
    this.lastTrackLayoutKey = '';
    this.lastCameraLayoutKey = '';
    this.displayedRound = 0;
    for (const token of this.playerTokens) token.destroy();
    this.playerTokens = [];
    for (const label of this.playerHudLabels) label?.destroy();
    this.playerHudLabels = [];
    this.animProxies = [];
    this.playerJokerBranchIndex = [];
    this.playerStartLaneIndex = [];
    this.playerJokerLapUsed = [];
  }

  private getPlayerTrackPos(playerIndex: number): PlayerTrackPosition {
    return {
      mainPathIndex: this.playerPathIndices[playerIndex] ?? 0,
      jokerBranchIndex: this.playerJokerBranchIndex[playerIndex] ?? null,
      startLaneIndex: this.playerStartLaneIndex[playerIndex] ?? null,
    };
  }

  private applyTrackPosToPlayer(playerIndex: number, pos: PlayerTrackPosition): void {
    this.playerPathIndices[playerIndex] = pos.mainPathIndex;
    this.playerJokerBranchIndex[playerIndex] = pos.jokerBranchIndex ?? null;
    this.playerStartLaneIndex[playerIndex] = pos.startLaneIndex ?? null;
  }

  create(): void {
    if (this.scene.isPaused()) this.scene.resume();
    this.cameras.main.setBackgroundColor(readGameCanvasColor());
    this.trackRenderer = new TrackRenderer(this, TRACK_CELL_SIZE);
    this.trackRenderer.setDepth(0);
    this.sceneReady = true;
    this.scale.off('resize', this.onScaleResize);
    this.scale.on('resize', this.onScaleResize);
    this.events.off('archive-replay-resize', this.onArchiveReplayResize);
    this.events.on('archive-replay-resize', this.onArchiveReplayResize);

    this.time.delayedCall(120, () => {
      this.cameraFitted = false;
      this.userViewAdjusted = false;
      this.refreshView(true);
      const turn = this.pendingTurn ?? (Number(this.registry.get('archiveInitialTurn')) || 1);
      this.pendingTurn = null;
      this.showTurn(turn);
    });

    const canvas = this.sys.game.canvas;
    if (this.wheelHandler) {
      canvas.removeEventListener('wheel', this.wheelHandler);
    }
    this.wheelHandler = (e: WheelEvent): void => {
      if (!this.scene.isActive()) return;
      e.preventDefault();
      this.userViewAdjusted = true;
      const cam = this.cameras.main;
      const canvas = this.sys.game.canvas;
      const rect = canvas.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const pointerY = e.clientY - rect.top;
      const worldX = cam.scrollX + pointerX / cam.zoom;
      const worldY = cam.scrollY + pointerY / cam.zoom;
      const sign = e.deltaY > 0 ? -1 : 1;
      const newZoom = Phaser.Math.Clamp(cam.zoom + sign * ZOOM_STEP, ZOOM_MIN, ZOOM_MAX);
      cam.setZoom(newZoom);
      cam.setScroll(worldX - pointerX / newZoom, worldY - pointerY / newZoom);
    };
    canvas.addEventListener('wheel', this.wheelHandler, { passive: false });

    this.input.on(
      'pointerdown',
      (ptr: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) => {
        if (currentlyOver.length > 0) return;
        if (ptr.leftButtonDown()) {
          this.isPanning = true;
          this.lastPanX = ptr.x;
          this.lastPanY = ptr.y;
        }
      }
    );
    this.input.on('pointermove', (ptr: Phaser.Input.Pointer) => {
      if (!this.isPanning) return;
      this.userViewAdjusted = true;
      const cam = this.cameras.main;
      const dx = ptr.x - this.lastPanX;
      const dy = ptr.y - this.lastPanY;
      cam.setScroll(cam.scrollX - dx / cam.zoom, cam.scrollY - dy / cam.zoom);
      this.lastPanX = ptr.x;
      this.lastPanY = ptr.y;
    });
    this.input.on('pointerup', () => {
      this.isPanning = false;
    });
  }

  /** Branche/débranche la vue 3D du replay — voir `ArchiveReplayBoard.ts`. */
  set3DSyncHandler(
    handler:
      | ((
          playerIndex: number,
          trackPos: PlayerTrackPosition,
          durationMs: number,
          options?: MovePlayerToOptions
        ) => void)
      | null
  ): void {
    this.sync3DHandler = handler;
  }

  /** Durée d’animation d’un tour de jeu (ms) — appliquée aux tours suivants. */
  setRoundDurationMs(ms: number): void {
    this.roundDurationMs = clampArchiveRoundMs(ms);
  }

  getRoundDurationMs(): number {
    return this.roundDurationMs;
  }

  /** Met à jour les bulles classement + nom au-dessus des pions (2D). */
  setPlayerHudLabels(round: number): void {
    if (!this.sceneReady) return;
    const ranking = rankingAtRound(this.replay, round, this.numParticipants);
    const rankByPlayer = new Map(ranking.map((r) => [r.playerIndex, r.rank]));
    for (let i = 0; i < this.numParticipants; i++) {
      const p = this.participant(i);
      const rankLabel = formatRaceRankLabel(rankByPlayer.get(i) ?? i + 1) ?? `${i + 1}e`;
      const name = labelOwnDriver(p?.driverName?.trim() || `Pilote ${i + 1}`, p?.driverId);
      const text = `${rankLabel}\n${name}`;
      let label = this.playerHudLabels[i];
      if (!label?.active) {
        label = this.add
          .text(0, 0, text, {
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            fontSize: '11px',
            fontStyle: 'bold',
            color: '#f8fafc',
            align: 'center',
            backgroundColor: '#0f172acc',
            padding: { x: 5, y: 3 },
          })
          .setOrigin(0.5, 1)
          .setDepth(HUD_LABEL_DEPTH);
        this.playerHudLabels[i] = label;
      } else {
        label.setText(text);
      }
      label.setVisible(true);
      this.positionHudLabel(i);
    }
    for (let i = this.numParticipants; i < this.playerHudLabels.length; i++) {
      this.playerHudLabels[i]?.destroy();
      this.playerHudLabels[i] = null;
    }
    this.playerHudLabels.length = this.numParticipants;
  }

  private positionHudLabel(playerIndex: number): void {
    const label = this.playerHudLabels[playerIndex];
    const token = this.playerTokens[playerIndex];
    if (!label?.active || !token?.active) return;
    const tokenH = token instanceof Phaser.GameObjects.Sprite ? token.displayHeight : 24;
    const offset = Math.max(18, tokenH * 0.55 + 8);
    label.setPosition(token.x, token.y - offset);
  }

  private syncPositionTo3D(
    playerIndex: number,
    trackPos: PlayerTrackPosition,
    durationMs: number,
    options?: MovePlayerToOptions
  ): void {
    this.sync3DHandler?.(playerIndex, trackPos, durationMs, options);
  }

  /** Position finale autoritaire (joker / grille inclus) — snap, sans reconstruit de trajet. */
  private syncBoardTo3D(board: RaceHistoryPlayerSnapshot[]): void {
    if (!this.sync3DHandler) return;
    for (const snap of board) {
      this.syncPositionTo3D(
        snap.playerIndex,
        {
          mainPathIndex: snap.pathIndex,
          jokerBranchIndex: snap.jokerBranchIndex ?? null,
          startLaneIndex: snap.startLaneIndex ?? null,
        },
        0,
        { snap: true }
      );
    }
  }

  zoomBy(delta: number): void {
    this.userViewAdjusted = true;
    const cam = this.cameras.main;
    cam.setZoom(Phaser.Math.Clamp(cam.zoom + delta, ZOOM_MIN, ZOOM_MAX));
  }

  resetCamera(): void {
    this.userViewAdjusted = false;
    this.cameraFitted = false;
    this.refreshView(true);
  }

  /** Affiche l’état du plateau après le tour de jeu, avec glissement des pions. */
  showTurn(round: number): void {
    if (this.scene.isPaused()) this.scene.resume();
    if (!this.sceneReady || !this.trackRenderer) {
      this.pendingTurn = round;
      return;
    }

    const board = resolveBoardAfterRound(this.replay, round, this.numParticipants);
    const prevRound = this.displayedRound;
    const hadTokens = this.playerTokens.some((t) => t?.active);
    const sameRound = round === this.displayedRound && hadTokens;

    const fromPositions = Array.from({ length: this.numParticipants }, (_, i) =>
      this.getPlayerTrackPos(i)
    );
    const fromJokerUsed = [...this.playerJokerLapUsed];
    const fromFinished = [...this.playerFinishedRace];
    const movesByPlayer = new Map(
      diceMovesForRound(this.replay, round, this.numParticipants).map((m) => [
        m.playerIndex,
        m.effectiveMove,
      ])
    );

    this.displayedRound = round;
    this.events.emit('archive-turn', round);

    const run = (): void => {
      const settle = (): void => {
        this.setPlayerHudLabels(this.displayedRound);
        this.events.emit('archive-turn-settled', this.displayedRound);
      };

      if (sameRound) {
        this.applyBoardSnapshots(board);
        this.layoutTokens();
        settle();
        return;
      }

      const forwardOrInitial = !hadTokens || round > prevRound;
      const shouldAnimate =
        forwardOrInitial &&
        this.circuit.path.length > 0 &&
        board.some((snap, i) =>
          this.shouldAnimatePlayer(fromPositions[i], fromFinished[i] ?? false, snap)
        );

      if (!shouldAnimate) {
        this.stopTokenAnimations();
        this.applyBoardSnapshots(board);
        this.ensureTokensThen(() => {
          this.layoutTokens();
          settle();
        });
        return;
      }

      this.animGeneration++;
      const gen = this.animGeneration;
      this.stopTokenAnimations();

      this.ensureTokensThen(() => {
        if (gen !== this.animGeneration) return;

        type RoundAnimPlan = {
          playerIndex: number;
          from: PlayerTrackPosition;
          to: PlayerTrackPosition;
          toFin: boolean;
          toJokerLapUsed: boolean | undefined;
          waypoints: PlayerTrackPosition[];
        };

        const plans: RoundAnimPlan[] = [];
        for (let i = 0; i < this.numParticipants; i++) {
          const snap = board.find((s) => s.playerIndex === i);
          if (!snap) continue;
          const from = fromPositions[i];
          const to = snapshotToTrackPosition(snap);
          const fromFin = fromFinished[i] ?? false;
          const toFin = snap.finished;

          if (trackPositionsEqual(from, to) && fromFin === toFin && this.playerTokens[i]?.active) {
            this.playerFinishedRace[i] = toFin;
            continue;
          }

          plans.push({
            playerIndex: i,
            from,
            to,
            toFin,
            toJokerLapUsed: snap.jokerLapUsed,
            waypoints: archiveWaypointsBetween(this.circuit, from, to, {
              fromJokerLapUsed: fromJokerUsed[i] ?? false,
              toJokerLapUsed: snap.jokerLapUsed,
              effectiveMove: movesByPlayer.get(i) ?? null,
            }),
          });
        }

        const maxSegments = Math.max(1, ...plans.map((p) => Math.max(1, p.waypoints.length - 1)));
        // Même horloge pour tous : le plus long (souvent joker) fixe la durée,
        // les trajets courts restent en mouvement jusqu’à la fin → pas d’arrêt.
        const roundAnimMs = archivePathDurationMs(this.roundDurationMs, maxSegments);

        let pending = 0;
        const doneOne = (): void => {
          pending--;
          if (pending > 0 || gen !== this.animGeneration) return;
          this.applyBoardSnapshots(board);
          this.layoutTokens();
          settle();
        };

        for (const plan of plans) {
          pending++;
          this.animateTokenAlongTrack(
            plan.playerIndex,
            plan.from,
            plan.to,
            plan.waypoints,
            roundAnimMs,
            () => {
              this.applyTrackPosToPlayer(plan.playerIndex, plan.to);
              this.playerJokerLapUsed[plan.playerIndex] =
                plan.toJokerLapUsed ?? this.playerJokerLapUsed[plan.playerIndex] ?? false;
              this.playerFinishedRace[plan.playerIndex] = plan.toFin;
              doneOne();
            }
          );
        }

        if (pending === 0) {
          this.applyBoardSnapshots(board);
          this.layoutTokens();
          settle();
        }
      });
    };

    this.ensureCarImagesThenLayout(run);
  }

  private shouldAnimatePlayer(
    from: PlayerTrackPosition,
    fromFinished: boolean,
    target: RaceHistoryPlayerSnapshot
  ): boolean {
    const to = snapshotToTrackPosition(target);
    if (fromFinished && target.finished) return !trackPositionsEqual(from, to);
    return !trackPositionsEqual(from, to);
  }

  private stopTokenAnimations(): void {
    for (const proxy of this.animProxies) {
      this.tweens.killTweensOf(proxy);
    }
    this.animProxies = [];
    for (const token of this.playerTokens) {
      if (token?.active) this.tweens.killTweensOf(token);
    }
  }

  private animateTokenAlongTrack(
    playerIndex: number,
    from: PlayerTrackPosition,
    to: PlayerTrackPosition,
    waypoints: PlayerTrackPosition[],
    durationMs: number,
    onComplete: () => void
  ): void {
    const token = this.playerTokens[playerIndex];
    if (!token?.active || waypoints.length <= 1) {
      this.applyTrackPosToPlayer(playerIndex, to);
      this.syncPositionTo3D(playerIndex, to, 0, { snap: true });
      onComplete();
      return;
    }

    const segments = Math.max(1, waypoints.length - 1);
    const perCell = durationMs / segments;
    const points = waypoints.map((wp) => {
      const layout = this.getTokenLayoutForTrackPos(playerIndex, wp);
      return { x: layout.x, y: layout.y };
    });
    const endLayout = this.getTokenLayoutForTrackPos(playerIndex, to);
    applyTokenDisplaySize(token, endLayout.displaySize);

    // Aligner la 3D sur `from` puis enfiler chaque case (direct = pas de cellsToward/wrap).
    this.syncPositionTo3D(playerIndex, from, 0, { snap: true });
    for (let i = 1; i < waypoints.length; i++) {
      this.syncPositionTo3D(playerIndex, waypoints[i], perCell, {
        direct: true,
        ease: 'linear',
      });
    }

    const proxy = { t: 0 };
    this.animProxies.push(proxy);
    let lastWpKey = '';
    this.tweens.add({
      targets: proxy,
      t: 1,
      duration: durationMs,
      ease: 'Linear',
      onUpdate: () => {
        const pos = lerpAlongPolyline(points, proxy.t);
        token.setPosition(pos.x, pos.y);
        this.positionHudLabel(playerIndex);
        const maxSeg = waypoints.length - 1;
        const wi = Math.min(maxSeg, Math.round(proxy.t * maxSeg));
        const wp = waypoints[wi];
        const key = `${wp.mainPathIndex}:${wp.jokerBranchIndex}:${wp.startLaneIndex}`;
        if (key !== lastWpKey) {
          this.applyTrackPosToPlayer(playerIndex, wp);
          lastWpKey = key;
        }
      },
      onComplete: () => {
        this.applyTrackPosToPlayer(playerIndex, to);
        token.setPosition(endLayout.x, endLayout.y);
        this.positionHudLabel(playerIndex);
        onComplete();
      },
    });
  }

  private getTokenLayoutForTrackPos(
    playerIndex: number,
    trackPos: PlayerTrackPosition
  ): TokenLayout {
    const occupants: CellOccupant[] = [];
    for (let j = 0; j < this.numParticipants; j++) {
      if (this.playerFinishedRace[j]) continue;
      occupants.push({
        id: j,
        stamp: j,
        position: this.getPlayerTrackPos(j),
      });
    }
    const extra: CellOccupant = {
      id: playerIndex,
      stamp: playerIndex,
      position: trackPos,
    };
    const gridKey = trackPositionGridKey(this.circuit, extra.position);
    const onCell = occupantsOnGridKey(this.circuit, occupants, gridKey, extra);
    const lead = onCell[0]?.position ?? trackPos;
    const forward = getTrackForwardDelta(this.circuit, lead);
    const layout = layoutsForOccupantsOnCell(
      onCell,
      forward,
      this.trackRenderer!.getCellSize(),
      'token'
    ).get(playerIndex) ?? { offsetX: 0, offsetY: 0, scale: 1 };
    const pos = this.trackRenderer!.getPositionForTrackPosition(this.circuit, trackPos);
    const displaySize = tokenDisplaySize(this.trackRenderer!.getCellSize(), layout.scale);
    return { x: pos.x + layout.offsetX, y: pos.y + layout.offsetY, displaySize };
  }

  private applyBoardSnapshots(board: RaceHistoryPlayerSnapshot[]): void {
    for (const snap of board) {
      const i = snap.playerIndex;
      if (i < 0 || i >= this.numParticipants) continue;
      this.applyTrackPosToPlayer(i, snapshotToTrackPosition(snap));
      this.playerJokerLapUsed[i] = snap.jokerLapUsed ?? false;
      this.playerFinishedRace[i] = snap.finished;
    }
    this.syncBoardTo3D(board);
  }

  private participant(playerIndex: number): RaceHistoryParticipant | undefined {
    return this.replay.participants.find((p) => p.playerIndex === playerIndex);
  }

  /** Clé texture Phaser — carId si dispo, sinon slot (carImagePath seul suffit). */
  private carTextureKey(p: RaceHistoryParticipant): string | null {
    const path = p.carImagePath?.trim();
    if (!path) return null;
    if (p.carId != null) return `archive-car-${p.carId}`;
    return `archive-car-p${p.playerIndex}`;
  }

  private carImageTextureKey(playerIndex: number): string | null {
    const p = this.participant(playerIndex);
    if (!p?.carImagePath?.trim()) return null;
    const archiveKey = this.carTextureKey(p);
    if (archiveKey && this.textures.exists(archiveKey)) return archiveKey;
    if (p.carId != null) {
      const liveKey = `car-${p.carId}`;
      if (this.textures.exists(liveKey)) return liveKey;
    }
    return null;
  }

  private collectCarTexturesToLoad(): { key: string; imagePath: string }[] {
    const out: { key: string; imagePath: string }[] = [];
    const seen = new Set<string>();
    for (const p of this.replay.participants) {
      const path = p.carImagePath?.trim();
      if (!path) continue;
      const key = this.carTextureKey(p);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      if (this.textures.exists(key)) continue;
      if (p.carId != null && this.textures.exists(`car-${p.carId}`)) continue;
      out.push({ key, imagePath: path });
    }
    return out;
  }

  private ensureTokensThen(onReady: () => void): void {
    if (!this.circuit?.path.length || !this.trackRenderer) return;
    for (let i = 0; i < this.numParticipants; i++) {
      const layout = this.getTokenLayoutForTrackPos(i, this.getPlayerTrackPos(i));
      let token = this.playerTokens[i];
      if (!token?.active) {
        token = this.createTokenForPlayer(i, layout.x, layout.y, layout.displaySize);
        this.playerTokens[i] = token;
      }
    }
    onReady();
  }

  private ensureCarImagesThenLayout(onReady?: () => void): void {
    const toLoad = this.collectCarTexturesToLoad();
    const finish = (): void => {
      this.carTexturesReady = true;
      this.upgradeTokensToCarSprites();
      onReady?.();
    };

    if (toLoad.length === 0) {
      finish();
      return;
    }

    if (this.load.isLoading()) {
      this.load.once('complete', () => this.ensureCarImagesThenLayout(onReady));
      return;
    }

    this.load.reset();
    this.load.crossOrigin = 'anonymous';
    for (const { key, imagePath } of toLoad) {
      this.load.image(key, resolveMediaUrl(imagePath));
    }
    this.load.once('complete', () => {
      for (const { key } of toLoad) {
        this.textures.get(key)?.setFilter(Phaser.Textures.FilterMode.LINEAR);
      }
      finish();
    });
    this.load.once('loaderror', finish);
    this.load.start();
  }

  /** Remplace les pions vectoriels par des sprites voiture dès que les textures sont prêtes. */
  private upgradeTokensToCarSprites(): void {
    if (!this.circuit?.path.length || !this.trackRenderer) return;
    for (let i = 0; i < this.numParticipants; i++) {
      if (!this.shouldUseCarSprite(i)) continue;
      const token = this.playerTokens[i];
      if (token instanceof Phaser.GameObjects.Sprite) continue;
      const layout = this.getTokenLayoutForTrackPos(i, this.getPlayerTrackPos(i));
      token?.destroy();
      this.playerTokens[i] = this.createTokenForPlayer(i, layout.x, layout.y, layout.displaySize);
    }
  }

  private shouldUseCarSprite(playerIndex: number): boolean {
    return this.carImageTextureKey(playerIndex) != null;
  }

  private refreshView(fitCamera: boolean): void {
    if (
      !this.scene.isActive() ||
      !this.sceneReady ||
      !this.circuit?.path.length ||
      !this.trackRenderer
    ) {
      return;
    }

    const cam = this.cameras.main;
    if (!cam) return;
    const w = cam.width;
    const h = cam.height;
    if (w < 64 || h < 64) {
      this.time.delayedCall(50, () => this.refreshView(fitCamera));
      return;
    }

    const layoutKey = `${w}x${h}`;
    this.trackRenderer.calculateOffset(this.circuit, w, h);
    if (layoutKey !== this.lastTrackLayoutKey) {
      this.lastTrackLayoutKey = layoutKey;
      this.trackRenderer.render(this.circuit);
    }

    if (layoutKey !== this.lastCameraLayoutKey) {
      this.lastCameraLayoutKey = layoutKey;
      if (!this.userViewAdjusted) {
        this.cameraFitted = false;
        fitCamera = true;
      }
    }

    if (fitCamera && !this.cameraFitted && !this.userViewAdjusted) {
      this.fitCameraToCircuit();
      this.cameraFitted = true;
    } else if (this.playerTokens.length >= this.numParticipants) {
      this.layoutTokens();
    }
  }

  private fitCameraToCircuit(): void {
    const cam = this.cameras.main;
    if (!this.trackRenderer) return;

    const cellSize = this.trackRenderer.getCellSize();
    const zoom = Phaser.Math.Clamp(
      calculateTrackZoomFit(this.circuit, cellSize, cam.width, cam.height, FIT_PADDING),
      ZOOM_MIN,
      ZOOM_MAX
    );
    cam.setZoom(zoom);

    const bounds = getCircuitDisplayBounds(this.circuit);
    if (bounds) {
      const { x: ox, y: oy } = this.trackRenderer.getOffset();
      const worldCx = ox + ((bounds.minX + bounds.maxX + 1) / 2) * cellSize;
      const worldCy = oy + ((bounds.minY + bounds.maxY + 1) / 2) * cellSize;
      cam.centerOn(worldCx, worldCy);
    } else {
      cam.centerOn(cam.width / 2, cam.height / 2);
    }
    this.layoutTokens();
  }

  private createTokenForPlayer(
    playerIndex: number,
    x: number,
    y: number,
    displaySize: number
  ): Phaser.GameObjects.Sprite | Phaser.GameObjects.Graphics {
    const color = PLAYER_COLORS[playerIndex % PLAYER_COLORS.length];
    const imageKey = this.carImageTextureKey(playerIndex);
    const token = imageKey
      ? createCarTokenFromImage(this, x, y, color, imageKey, displaySize)
      : createCarToken(this, x, y, color, this.tokenStyle, displaySize);
    token.setDepth(TOKEN_DEPTH);
    applyTokenHitArea(token);
    return token;
  }

  private layoutTokens(): void {
    if (!this.circuit?.path.length || !this.trackRenderer) return;

    for (let i = 0; i < this.numParticipants; i++) {
      const layout = this.getTokenLayoutForTrackPos(i, this.getPlayerTrackPos(i));
      let token = this.playerTokens[i];
      const wantSprite = this.shouldUseCarSprite(i);
      const hasSprite = token instanceof Phaser.GameObjects.Sprite;
      if (!token?.active || wantSprite !== hasSprite) {
        token?.destroy();
        token = this.createTokenForPlayer(i, layout.x, layout.y, layout.displaySize);
        this.playerTokens[i] = token;
      } else {
        token.setPosition(layout.x, layout.y);
        applyTokenDisplaySize(token, layout.displaySize);
        token.setDepth(TOKEN_DEPTH);
        token.setVisible(true);
        token.setAlpha(this.playerFinishedRace[i] ? 0.55 : 1);
      }
    }

    for (let i = this.numParticipants; i < this.playerTokens.length; i++) {
      this.playerTokens[i]?.destroy();
    }
    this.playerTokens.length = this.numParticipants;

    for (let i = 0; i < this.numParticipants; i++) {
      this.positionHudLabel(i);
    }
  }
}
